/*
 * Vision Wellness Quiz engine
 * ---------------------------------------------------------------------------
 * Dependency-free, multi-instance. Drives the one-question-per-screen flow,
 * client-side scoring, profile-band mapping, consent gating, Klaviyo capture
 * and Meta tracking for the `vision-wellness-quiz` section.
 *
 * Scoring model (from the Vision Wellness Assessment v3 spec):
 *   final = base(Q1-Q3) + additive(Q4,Q5,Q7) + subtractive(Q8-Q11)
 * Protective (subtractive) answers carry NEGATIVE points, so the engine only
 * ever sums the selected values and floors the result at 0. Bands are matched
 * by ascending minimum threshold, which tolerates half-point scores without
 * falling into a gap between integer ranges.
 *
 * No scoring key, point value, or profile band is ever written to the URL or
 * to the DOM the shopper reads. Point values live only on hidden input values.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-vwq]';
  var STORAGE_PREFIX = 'vwq:';

  /** Safe number parse: blank/NaN -> 0. */
  function toNumber(value) {
    var n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  }

  /** Fire a Meta Pixel custom event when tracking is enabled and fbq exists. */
  function trackPixel(enabled, event, payload) {
    if (!enabled) return;
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', event, payload || {});
      }
    } catch (err) {
      /* tracking must never break the quiz */
    }
  }

  /** Fire a Meta Pixel STANDARD event (e.g. Lead) when enabled and fbq exists. */
  function trackStandard(enabled, event, payload) {
    if (!enabled) return;
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', event, payload || {});
      }
    } catch (err) {
      /* tracking must never break the quiz */
    }
  }

  /** Dispatch a DOM event so themes / GTM / CAPI bridges can subscribe. */
  function emit(root, name, detail) {
    try {
      root.dispatchEvent(
        new CustomEvent('vwq:' + name, { bubbles: true, detail: detail || {} })
      );
    } catch (err) {
      /* older browsers without CustomEvent constructor: ignore */
    }
  }

  function VisionWellnessQuiz(root) {
    this.root = root;
    this.id = root.getAttribute('data-vwq-id') || 'quiz';
    this.trackingEnabled = root.getAttribute('data-tracking') === 'true';
    this.floorScore = root.getAttribute('data-floor-score') !== 'false';
    this.rounding = root.getAttribute('data-rounding') || 'nearest';
    this.resultLeadEvent = root.getAttribute('data-result-lead-event') !== 'false';
    this.leadEventName = root.getAttribute('data-lead-event-name') || 'Lead';
    this.emailPosition = root.getAttribute('data-email-position') || 'after_result';
    this.persist = root.getAttribute('data-persist') === 'true';

    this.screens = {};
    var screenEls = root.querySelectorAll('[data-screen]');
    for (var i = 0; i < screenEls.length; i++) {
      this.screens[screenEls[i].getAttribute('data-screen')] = screenEls[i];
    }

    this.questions = Array.prototype.slice.call(
      root.querySelectorAll('[data-question]')
    );
    this.total = this.questions.length;
    this.index = 0;
    this.answered = {};

    this.progressBar = root.querySelector('[data-progress-bar]');
    this.progressLabel = root.querySelector('[data-progress-label]');
    this.liveRegion = root.querySelector('[data-live]');

    this.init();
  }

  VisionWellnessQuiz.prototype.init = function () {
    var self = this;

    // Start / restart
    this.on('[data-action="start"]', 'click', function (e) {
      e.preventDefault();
      self.start();
    });
    this.on('[data-action="restart"]', 'click', function (e) {
      e.preventDefault();
      self.restart();
    });
    this.on('[data-action="next"]', 'click', function (e) {
      e.preventDefault();
      self.next();
    });
    this.on('[data-action="back"]', 'click', function (e) {
      e.preventDefault();
      self.back();
    });

    // Auto-advance on answer selection (with a short beat so the choice reads
    // as confirmed). Older users benefit from the visible confirmation.
    this.questions.forEach(function (q, qi) {
      var inputs = q.querySelectorAll('input[type="radio"]');
      Array.prototype.forEach.call(inputs, function (input) {
        input.addEventListener('change', function () {
          self.answered[qi] = true;
          self.refreshNav();
          self.emitProgress();
          if (q.getAttribute('data-autoadvance') !== 'false') {
            window.setTimeout(function () {
              if (self.index === qi) self.next();
            }, 260);
          }
        });
      });
    });

    this.bindEmailForm();
    this.restoreProgress();
    this.showScreen(this.startedScreen || 'intro');
  };

  VisionWellnessQuiz.prototype.on = function (selector, evt, handler) {
    var els = this.root.querySelectorAll(selector);
    Array.prototype.forEach.call(els, function (el) {
      el.addEventListener(evt, handler);
    });
  };

  VisionWellnessQuiz.prototype.showScreen = function (name) {
    for (var key in this.screens) {
      if (this.screens.hasOwnProperty(key)) {
        var isActive = key === name;
        this.screens[key].hidden = !isActive;
        this.screens[key].setAttribute('aria-hidden', String(!isActive));
      }
    }
    this.currentScreen = name;
    // Move focus to the screen heading for screen-reader continuity.
    var focusTarget = this.screens[name]
      ? this.screens[name].querySelector('[data-focus]')
      : null;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  };

  VisionWellnessQuiz.prototype.start = function () {
    this.index = 0;
    this.finished = false;
    this.showScreen('quiz');
    this.showQuestion(0);
    trackPixel(this.trackingEnabled, 'QuizStart', { quiz_id: this.id });
    emit(this.root, 'start', { quizId: this.id });
  };

  VisionWellnessQuiz.prototype.restart = function () {
    this.answered = {};
    this.questions.forEach(function (q) {
      var inputs = q.querySelectorAll('input[type="radio"]');
      Array.prototype.forEach.call(inputs, function (input) {
        input.checked = false;
      });
    });
    this.clearProgress();
    this.index = 0;
    this.start();
  };

  VisionWellnessQuiz.prototype.showQuestion = function (i) {
    this.index = i;
    this.questions.forEach(function (q, qi) {
      q.hidden = qi !== i;
      q.setAttribute('aria-hidden', String(qi !== i));
    });
    this.updateProgress();
    this.refreshNav();

    // Focus the fieldset / first control for keyboard + SR users.
    var current = this.questions[i];
    var legendFocus = current.querySelector('[data-focus]');
    if (legendFocus && typeof legendFocus.focus === 'function') {
      legendFocus.focus();
    }
    this.saveProgress();
  };

  VisionWellnessQuiz.prototype.next = function () {
    if (!this.answered[this.index]) {
      this.flagRequired();
      return;
    }
    if (this.index < this.total - 1) {
      this.showQuestion(this.index + 1);
    } else {
      this.finish();
    }
  };

  VisionWellnessQuiz.prototype.back = function () {
    if (this.currentScreen === 'result' || this.currentScreen === 'email') {
      this.showScreen('quiz');
      this.showQuestion(this.total - 1);
      return;
    }
    if (this.index > 0) {
      this.showQuestion(this.index - 1);
    } else {
      this.showScreen('intro');
    }
  };

  VisionWellnessQuiz.prototype.flagRequired = function () {
    var current = this.questions[this.index];
    if (current) {
      current.setAttribute('data-error', 'true');
      var msg = current.querySelector('[data-question-error]');
      if (msg) msg.hidden = false;
    }
    if (this.liveRegion) {
      this.liveRegion.textContent =
        current && current.getAttribute('data-error-text')
          ? current.getAttribute('data-error-text')
          : 'Please choose an answer to continue.';
    }
  };

  VisionWellnessQuiz.prototype.refreshNav = function () {
    var current = this.questions[this.index];
    if (current && this.answered[this.index]) {
      current.removeAttribute('data-error');
      var msg = current.querySelector('[data-question-error]');
      if (msg) msg.hidden = true;
    }
    var nextBtns = this.root.querySelectorAll('[data-action="next"]');
    Array.prototype.forEach.call(nextBtns, function (btn) {
      btn.textContent =
        btn.getAttribute('data-label-' + (this.isLast() ? 'last' : 'next')) ||
        btn.textContent;
    }.bind(this));
  };

  VisionWellnessQuiz.prototype.isLast = function () {
    return this.index === this.total - 1;
  };

  VisionWellnessQuiz.prototype.updateProgress = function () {
    var step = this.index + 1;
    var pct = this.total ? Math.round((step / this.total) * 100) : 0;
    if (this.progressBar) {
      this.progressBar.style.setProperty('--vwq-progress', pct + '%');
      this.progressBar.setAttribute('aria-valuenow', String(step));
      this.progressBar.setAttribute('aria-valuemax', String(this.total));
    }
    if (this.progressLabel) {
      var tpl = this.progressLabel.getAttribute('data-template') || '{step} / {total}';
      this.progressLabel.textContent = tpl
        .replace('{step}', String(step))
        .replace('{total}', String(this.total))
        .replace('{percent}', String(pct));
    }
  };

  VisionWellnessQuiz.prototype.emitProgress = function () {
    emit(this.root, 'progress', {
      quizId: this.id,
      step: this.index + 1,
      total: this.total
    });
  };

  /** Sum selected point values. Never surfaced to the shopper. */
  VisionWellnessQuiz.prototype.computeScore = function () {
    var score = 0;
    this.questions.forEach(function (q) {
      var checked = q.querySelector('input[type="radio"]:checked');
      if (checked) score += toNumber(checked.value);
    });
    if (this.floorScore && score < 0) score = 0;
    // Half-point protective factors can land a score between integer bands
    // (e.g. 9.5, 16.5). Resolve the gap deterministically. Default 'nearest'
    // rounds .5 up, moving a shopper toward the higher support tier.
    switch (this.rounding) {
      case 'none':
        break;
      case 'up':
        score = Math.ceil(score);
        break;
      case 'down':
        score = Math.floor(score);
        break;
      default:
        score = Math.round(score);
    }
    return score;
  };

  /** Pick the band whose ascending min threshold is the highest <= score. */
  VisionWellnessQuiz.prototype.resolveBand = function (score) {
    var bands = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-result-band]')
    );
    bands.sort(function (a, b) {
      return toNumber(a.getAttribute('data-min')) - toNumber(b.getAttribute('data-min'));
    });
    var match = bands.length ? bands[0] : null;
    bands.forEach(function (band) {
      if (score >= toNumber(band.getAttribute('data-min'))) match = band;
    });
    return match;
  };

  VisionWellnessQuiz.prototype.finish = function () {
    var self = this;
    // Idempotent per run: a double-click / Enter must not re-fire completion.
    if (this.finished) return;
    this.finished = true;
    var score = this.computeScore();
    var band = this.resolveBand(score);

    // Reveal only the matched band.
    var bands = this.root.querySelectorAll('[data-result-band]');
    Array.prototype.forEach.call(bands, function (b) {
      var active = b === band;
      b.hidden = !active;
      b.setAttribute('aria-hidden', String(!active));
    });

    var profile = band ? band.getAttribute('data-profile') || '' : '';

    // Remember the outcome so the result-arrival event can fire at the exact
    // moment the band is shown (which differs across email flows).
    this.lastProfile = profile;
    this.lastScore = score;
    this.resultAnnounced = false;

    // Analyzing beat (skipped under reduced motion).
    var reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var showResult = function () {
      if (self.emailPosition === 'before_result' && self.hasEmailStep()) {
        self.pendingResult = true;
        self.showScreen('email');
      } else {
        self.showScreen('result');
        if (self.emailPosition === 'after_result') self.revealInlineEmail();
        self.onResultShown();
      }
      trackPixel(self.trackingEnabled, 'QuizComplete', {
        quiz_id: self.id,
        profile: profile
      });
      emit(self.root, 'complete', { quizId: self.id, profile: profile });
      self.clearProgress();
    };

    if (this.screens.analyzing && !reduce) {
      this.showScreen('analyzing');
      var delay = toNumber(this.screens.analyzing.getAttribute('data-delay')) || 1600;
      window.setTimeout(showResult, delay);
    } else {
      showResult();
    }
  };

  VisionWellnessQuiz.prototype.hasEmailStep = function () {
    return !!this.screens.email;
  };

  VisionWellnessQuiz.prototype.revealInlineEmail = function () {
    var inline = this.root.querySelector('[data-inline-email]');
    if (inline) inline.hidden = false;
  };

  /**
   * Fires once when the shopper arrives at the result band. Emits a Meta
   * standard Lead event (a completed, profiled assessment is a qualified
   * lead) and a `vwq:result` DOM event carrying the profile + score, for
   * GTM / CAPI bridges. Guarded so it never double-counts within one run.
   */
  VisionWellnessQuiz.prototype.onResultShown = function () {
    if (this.resultAnnounced) return;
    this.resultAnnounced = true;
    trackStandard(this.trackingEnabled && this.resultLeadEvent, this.leadEventName, {
      content_name: this.lastProfile,
      content_category: 'vision_wellness_quiz',
      quiz_id: this.id,
      value: this.lastScore
    });
    emit(this.root, 'result', {
      quizId: this.id,
      profile: this.lastProfile,
      score: this.lastScore
    });
  };

  // --- Email capture + consent -------------------------------------------

  VisionWellnessQuiz.prototype.bindEmailForm = function () {
    var self = this;
    var forms = this.root.querySelectorAll('[data-email-form]');
    Array.prototype.forEach.call(forms, function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submitEmail(form);
      });
    });
  };

  VisionWellnessQuiz.prototype.submitEmail = function (form) {
    var self = this;
    var emailInput = form.querySelector('input[type="email"]');
    var email = emailInput ? emailInput.value.trim() : '';
    var errorEl = form.querySelector('[data-email-error]');

    var showError = function (message) {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
    };

    // Optional first / last name (may be absent when name collection is off).
    var firstInput = form.querySelector('input[name="first_name"]');
    var lastInput = form.querySelector('input[name="last_name"]');
    var firstName = firstInput ? firstInput.value.trim() : '';
    var lastName = lastInput ? lastInput.value.trim() : '';
    var nameRequired =
      (firstInput && firstInput.required) || (lastInput && lastInput.required);

    // Validate name (before email so the first empty field is flagged in order).
    if (nameRequired && (!firstName || !lastName)) {
      showError(form.getAttribute('data-error-name') || 'Please enter your first and last name.');
      var emptyName = !firstName ? firstInput : lastInput;
      if (emptyName) {
        emptyName.setAttribute('aria-invalid', 'true');
        emptyName.focus();
      }
      return;
    }
    if (firstInput) firstInput.removeAttribute('aria-invalid');
    if (lastInput) lastInput.removeAttribute('aria-invalid');

    // Validate email.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError(form.getAttribute('data-error-email') || 'Enter a valid email address.');
      if (emailInput) emailInput.focus();
      return;
    }

    // Required consent checkboxes must be checked (WMHMDA / marketing).
    var requiredConsents = form.querySelectorAll('input[data-consent][required]');
    var missing = false;
    Array.prototype.forEach.call(requiredConsents, function (box) {
      if (!box.checked) missing = true;
    });
    if (missing) {
      showError(form.getAttribute('data-error-consent') || 'Please confirm the boxes below to continue.');
      return;
    }

    if (errorEl) errorEl.hidden = true;
    if (emailInput) emailInput.removeAttribute('aria-invalid');

    // Collect which consent boxes were checked, so the CRM can tag the source.
    var consents = {};
    var boxes = form.querySelectorAll('input[data-consent]');
    Array.prototype.forEach.call(boxes, function (box) {
      consents[box.getAttribute('data-consent')] = box.checked;
    });

    var band = this.root.querySelector('[data-result-band]:not([hidden])');
    var profile = band ? band.getAttribute('data-profile') || '' : '';

    var listId = this.root.getAttribute('data-klaviyo-list') || '';
    var sourceTag = this.root.getAttribute('data-klaviyo-source-tag') || '';

    this.setLoading(form, true);

    // PII travels only in the DOM lead event (for the server-side CAPI bridge
    // to hash) and to Klaviyo. It is never placed in the client Meta Pixel
    // custom data or in any URL.
    var leadDetail = {
      quizId: self.id,
      profile: profile,
      score: self.lastScore,
      consents: consents,
      email: email,
      firstName: firstName,
      lastName: lastName
    };

    this.sendToKlaviyo(email, listId, {
      first_name: firstName,
      last_name: lastName,
      vwq_profile: profile,
      vwq_source: sourceTag,
      vwq_consents: consents
    })
      .then(function () {
        self.setLoading(form, false);
        trackPixel(self.trackingEnabled, 'QuizLead', {
          quiz_id: self.id,
          profile: profile
        });
        emit(self.root, 'lead', leadDetail);
        self.onEmailCaptured(form);
      })
      ['catch'](function () {
        // Network / API failure: do not trap the shopper. Log the lead as an
        // event for the storefront CAPI bridge and continue to the result.
        self.setLoading(form, false);
        var deferred = {};
        for (var k in leadDetail) { if (leadDetail.hasOwnProperty(k)) deferred[k] = leadDetail[k]; }
        deferred.deferred = true;
        emit(self.root, 'lead', deferred);
        self.onEmailCaptured(form);
      });
  };

  VisionWellnessQuiz.prototype.setLoading = function (form, loading) {
    var btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    if (loading) {
      btn.setAttribute('data-loading', 'true');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      btn.removeAttribute('data-loading');
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  };

  VisionWellnessQuiz.prototype.onEmailCaptured = function (form) {
    var success = form.querySelector('[data-email-success]');
    var fields = form.querySelector('[data-email-fields]');
    if (success) success.hidden = false;
    if (fields) fields.hidden = true;

    if (this.emailPosition === 'before_result' && this.pendingResult) {
      this.pendingResult = false;
      this.showScreen('result');
      this.onResultShown();
    }
  };

  /**
   * Subscribe via Klaviyo's client-side Subscribe endpoint when a public API
   * key + list id are configured. Returns a promise. Consent is passed so the
   * profile carries the correct marketing subscription state.
   */
  VisionWellnessQuiz.prototype.sendToKlaviyo = function (email, listId, meta) {
    var publicKey = this.root.getAttribute('data-klaviyo-key') || '';

    if (!publicKey || !listId) {
      // No credentials configured: resolve so the DOM `vwq:lead` event is the
      // integration point (theme/GTM/CAPI can pick it up server-side).
      return Promise.resolve({ skipped: true });
    }

    var url =
      'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(publicKey);

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: meta.vwq_source || 'Vision Wellness Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: meta.first_name || undefined,
                last_name: meta.last_name || undefined,
                properties: {
                  vwq_profile: meta.vwq_profile,
                  vwq_consents: meta.vwq_consents
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok && res.status !== 202) {
        throw new Error('Klaviyo subscribe failed: ' + res.status);
      }
      return res;
    });
  };

  // --- Progress persistence (opt-in) -------------------------------------

  VisionWellnessQuiz.prototype.storageKey = function () {
    return STORAGE_PREFIX + this.id;
  };

  VisionWellnessQuiz.prototype.saveProgress = function () {
    if (!this.persist) return;
    try {
      var answers = {};
      this.questions.forEach(function (q, qi) {
        var checked = q.querySelector('input[type="radio"]:checked');
        if (checked) answers[qi] = checked.value;
      });
      window.localStorage.setItem(
        this.storageKey(),
        JSON.stringify({ index: this.index, answers: answers })
      );
    } catch (err) {
      /* storage may be unavailable (private mode); ignore */
    }
  };

  VisionWellnessQuiz.prototype.restoreProgress = function () {
    if (!this.persist) return;
    try {
      var raw = window.localStorage.getItem(this.storageKey());
      if (!raw) return;
      var state = JSON.parse(raw);
      var self = this;
      Object.keys(state.answers || {}).forEach(function (qi) {
        var q = self.questions[qi];
        if (!q) return;
        var input = q.querySelector(
          'input[type="radio"][value="' + state.answers[qi] + '"]'
        );
        if (input) {
          input.checked = true;
          self.answered[qi] = true;
        }
      });
      if (Object.keys(state.answers || {}).length) {
        this.startedScreen = 'quiz';
        this.index = state.index || 0;
        window.setTimeout(function () {
          self.showQuestion(self.index);
        }, 0);
      }
    } catch (err) {
      /* corrupt state: ignore and start fresh */
    }
  };

  VisionWellnessQuiz.prototype.clearProgress = function () {
    if (!this.persist) return;
    try {
      window.localStorage.removeItem(this.storageKey());
    } catch (err) {
      /* ignore */
    }
  };

  // --- Boot --------------------------------------------------------------

  function boot(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-vwq-ready') === 'true') return;
      root.setAttribute('data-vwq-ready', 'true');
      /* eslint-disable no-new */
      new VisionWellnessQuiz(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Re-init inside the Shopify theme editor when a section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });

  window.VisionWellnessQuiz = VisionWellnessQuiz;
})();
